import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle2, XCircle } from 'lucide-react'

/**
 * Test page for the new React-based Visual Editor
 * Loads the IIFE bundle dynamically and tests it on a mock store page
 */
export function ReactEditorTest() {
  const [bundleLoaded, setBundleLoaded] = useState(false)
  const [editorActive, setEditorActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadBundle = () => {
    console.log('[Test] Loading React editor bundle...')
    setError(null)

    // Remove old script if exists
    const oldScript = document.querySelector('script[src*="visual-editor"]')
    if (oldScript) {
      oldScript.remove()
    }

    // Load from public folder with cache busting
    const script = document.createElement('script')
    script.src = `/visual-editor.js?t=${Date.now()}`
    script.onload = () => {
      console.log('[Test] ✅ Bundle loaded')
      setBundleLoaded(true)
    }
    script.onerror = () => {
      const err = 'Failed to load bundle. Make sure you built it with: npm run build:editor'
      console.error('[Test]', err)
      setError(err)
    }
    document.head.appendChild(script)
  }

  const initEditor = () => {
    if (!(window as any).TNXReactEditor) {
      setError('Bundle not loaded yet')
      return
    }

    try {
      (window as any).TNXReactEditor.init()
      setEditorActive(true)
      console.log('[Test] ✅ Editor initialized')
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err)
      console.error('[Test] ❌ Failed to initialize:', err)
      setError(errMsg)
    }
  }

  const destroyEditor = () => {
    if (!(window as any).TNXReactEditor) {
      return
    }

    try {
      (window as any).TNXReactEditor.destroy()
      setEditorActive(false)
      console.log('[Test] ✅ Editor destroyed')
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err)
      console.error('[Test] ❌ Failed to destroy:', err)
      setError(errMsg)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Test Controls */}
      <div className="fixed top-4 right-4 z-50">
        <Card className="w-80">
          <CardHeader>
            <CardTitle className="text-lg">React Editor Test</CardTitle>
            <CardDescription>Load and test the visual editor bundle</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Status Indicators */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                {bundleLoaded ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-slate-400" />
                )}
                <span>Bundle {bundleLoaded ? 'Loaded' : 'Not Loaded'}</span>
              </div>
              <div className="flex items-center gap-2">
                {editorActive ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-slate-400" />
                )}
                <span>Editor {editorActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={loadBundle}
                disabled={bundleLoaded}
                className="w-full"
                variant={bundleLoaded ? 'outline' : 'default'}
              >
                {bundleLoaded ? 'Bundle Loaded' : 'Load Bundle'}
              </Button>

              <Button
                onClick={initEditor}
                disabled={!bundleLoaded || editorActive}
                className="w-full"
                variant={editorActive ? 'outline' : 'default'}
              >
                {editorActive ? 'Editor Active' : 'Initialize Editor'}
              </Button>

              <Button
                onClick={destroyEditor}
                disabled={!editorActive}
                className="w-full"
                variant="destructive"
              >
                Destroy Editor
              </Button>
            </div>

            {/* Instructions */}
            <div className="text-xs text-muted-foreground border-t pt-3">
              <p className="font-semibold mb-1">Instructions:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Click "Load Bundle"</li>
                <li>Click "Initialize Editor"</li>
                <li>Click "Edit" mode in bottom ribbon</li>
                <li>Hover over elements to highlight</li>
                <li>Click elements to edit</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mock Store Page */}
      <div className="container max-w-6xl py-8 mb-80">
        {/* Store Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Mi Tienda Demo</h1>
          <p className="text-slate-600 mt-2">Bienvenido a nuestra tienda online</p>
        </div>

        {/* Banner */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg p-8 mb-6 text-center">
          <h2 className="text-4xl font-bold mb-2">¡Ofertas de Temporada!</h2>
          <p className="text-xl opacity-90">Hasta 50% de descuento en productos seleccionados</p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { emoji: '👕', name: 'Camiseta Premium', price: '$29.99' },
            { emoji: '👟', name: 'Zapatillas Deportivas', price: '$79.99' },
            { emoji: '🎒', name: 'Mochila Urbana', price: '$49.99' },
            { emoji: '⌚', name: 'Reloj Inteligente', price: '$199.99' },
            { emoji: '🎧', name: 'Auriculares Bluetooth', price: '$89.99' },
            { emoji: '📱', name: 'Funda para Móvil', price: '$19.99' },
          ].map((product, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 h-48 flex items-center justify-center text-6xl">
                {product.emoji}
              </div>
              <div className="p-4">
                <h3 className="product-name font-semibold text-lg mb-2 text-slate-900">{product.name}</h3>
                <p className="product-price text-2xl font-bold text-purple-600 mb-4">{product.price}</p>
                <button className="btn-primary js-addtocart w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                  Agregar al Carrito
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h3 className="text-xl font-bold mb-4">Sobre Nosotros</h3>
          <p className="text-slate-600">
            Somos una tienda comprometida con la calidad y el servicio al cliente.
            Ofrecemos los mejores productos al mejor precio.
          </p>
        </div>
      </div>
    </div>
  )
}
